import { navigate, type Screen } from '@/app'
import { resetAge } from '@/lib/ageGate'
import { clear, el, setText } from '@/lib/dom'
import { T } from '@/lib/i18n'
import { LANGUAGES, readLanguage, reloadForLanguage, writeLanguage } from '@/lib/language'
import {
  applyViewerAgeLimit,
  getProfilesResult,
  isRealProfileId,
  type Profile,
  readSelectedProfileId,
  verifyProfilePin,
  writeSelectedProfileId,
} from '@/lib/profiles'
import { drawQr, encodeQr } from '@/lib/qr'
import {
  AUTH_MODE,
  deviceLabel,
  pollQrLogin,
  type QrStart,
  readSession,
  sessionGeneration,
  signOut,
  startQrLogin,
  writeSession,
} from '@/lib/session'
import { createPinPad, type PinPad } from '@/ui/pinPad'
import { repaintWebNav } from '@/browser/shell'
import { webButton, webChip, webEmpty, webPage, webScreen, webTitle } from '@/browser/ui'

/**
 * Hesab ekranları — QR girişi · «Kim baxır?» · Profil.
 *
 * ⚠ QR GİRİŞİ BURADA DA YEGANƏ GİRİŞ YOLUDUR (MYTasks 214 · sahib təsdiqi
 *   2026-08-10: «bəli ayrıca dizayn və QR code ilə giriş»). Brauzerdə parol
 *   formu qurmaq texniki olaraq mümkün idi, amma:
 *     · TV brauzerində mətn yazmaq hələ də əziyyətlidir (ekran klaviaturası),
 *     · parol axını iki səthdə iki cür saxlanılmalı olardı (cihaz app-ında forma
 *       ümumiyyətlə yoxdur),
 *     · cihaz limiti/sessiya idarəsi QR axınına bağlıdır (`RegisterDevice`).
 *   Yəni fərq YALNIZ dizayndadır, axın eynidir.
 */

/** Poll intervalı — `setTimeout`, `setInterval` DEYİL (cavab gecikəndə üst-üstə yığılmasın). */
const POLL_INTERVAL_MS = 3000

/**
 * Nömrəli addım sətri qurur.
 *
 * ⚠ Nömrə MƏTNDƏN AYRIDIR (bax çağırış yerindəki ⚠) — uzun sətir sınanda ikinci
 *   sətir nişanın altına deyil, mətnin öz sütununda qalır.
 */
function addStep(host: HTMLElement, index: string, text: string): void {
  const row = el('div', 'web-qr__step')
  row.appendChild(el('span', 'web-qr__step-num', index))
  row.appendChild(el('span', 'web-qr__step-text', text))
  host.appendChild(row)
}

export function createWebQrScreen(onSignedIn: () => void): Screen {
  /*
    ⚠⚠ MAKET İKİ SÜTUNDUR, MƏRKƏZƏ YIĞILMIŞ SÜTUN DEYİL (2026-08-12 sahib qeydi:
       «veb tv projeni necə gəldi basmısan, biraz düzəliş et dizaynına formasına»).

       ƏVVƏL: hər şey — başlıq, QR, kod, addımlar — bir sütunda MƏRKƏZƏ yığılırdı
       və 1600×900 brauzer pəncərəsində kadrın yarısı boş qara sahə qalırdı.
       Üstəlik addımlar da mərkəzləndiyi üçün sol kənar pilləli çıxırdı — üç
       sətrin heç biri eyni yerdən başlamırdı, yəni siyahı «siyahı» kimi oxunmurdu.

       İNDİ: QR + kod SOLDA, izahat SAĞDA. Brauzer pəncərəsi eninədir (TV
       brauzeri həmişə landşaftdır), yəni iki sütun boşluğu doldurur və göz
       «skan et → kodu təsdiqlə» ardıcıllığını soldan sağa oxuyur.
    ⚠ TEXNİKA `inline-block`-dur, Grid/flex-gap YOX: bu app chrome53-ə yığılır
      (bax `vite.config.ts` LEGACY_TARGET) və PERFORMANCE.md həmin qadağanı
      qoyur. Fayldakı bütün digər maketlər də eyni üsuldadır.
  */
  const page = el('div', 'web-qr')

  const card = el('div', 'web-qr__card')
  page.appendChild(card)

  // Brend nişanı — ekranda app-ın adı heç yerdə görünmürdü.
  const brand = el('div', 'web-qr__brand')
  const logo = document.createElement('img')
  logo.src = './logo.svg'
  logo.alt = 'Kinobox'
  brand.appendChild(logo)
  card.appendChild(brand)

  card.appendChild(el('h1', 'web-qr__title', T('Telefonunla daxil ol')))
  const subtitle = el('p', 'web-qr__sub', T('Kod hazırlanır…'))
  card.appendChild(subtitle)

  const cols = el('div', 'web-qr__cols')
  card.appendChild(cols)

  const left = el('div', 'web-qr__col web-qr__col--qr')
  cols.appendChild(left)

  const qrHolder = el('div', 'web-qr__frame')
  left.appendChild(qrHolder)

  const codeBox = el('div', 'web-qr__code', '········')
  left.appendChild(codeBox)

  const right = el('div', 'web-qr__col web-qr__col--steps')
  cols.appendChild(right)

  const steps = el('div', 'web-qr__steps')
  right.appendChild(steps)

  const note = el('p', 'web-qr__note', T('Kod bir neçə dəqiqə sonra yenilənir.'))
  right.appendChild(note)

  let pollTimer = 0
  let stopped = false
  let start: QrStart | null = null
  /** `startQrLogin` uçuşdadır — paralel ikinci sorğu getməsin (izah `check()`-dədir). */
  let starting = false
  /** Start olmadan keçən tiklər — hər 3-cüdə yenidən cəhd. */
  let missedTicks = 0

  function render(session: QrStart): void {
    start = session
    setText(subtitle, T('Telefonunda aşağıdakı ünvanı aç və kodu təsdiqlə'))
    setText(codeBox, session.code)

    clear(steps)
    // ⚠ NÖMRƏ AYRICA ELEMENTDƏDİR, mətnin içində «1. » kimi deyil: mətn sətri
    //   sınanda ikinci sətir nömrənin ALTINA düşürdü. İndi nişan sabit enlidir
    //   və mətn onun sağında bloklu qalır.
    addStep(steps, '1', T('QR-i telefonunun kamerası ilə skan et'))
    addStep(steps, '2', T('və ya ') + displayUrl(session.verificationUrl) + T(' ünvanını aç'))
    addStep(steps, '3', T('yuxarıdakı kodu daxil edib təsdiqlə'))

    clear(qrHolder)
    const matrix = encodeQr(session.verificationUrl, 'M')
    if (matrix) {
      // ⚠ Modul ölçüsü cihaz ekranından BÖYÜKDÜR (8 → 6, amma kətan miqyaslanmır):
      //   brauzerdə istifadəçi ekrana yaxın da ola bilər, uzaq da — orta ölçü hər
      //   iki məsafədə skan olunur. QR çəkilməsə də axın işləkdir (ünvan + kod).
      const canvas = drawQr(matrix, 6, 2)
      canvas.className = 'web-qr__canvas'
      qrHolder.appendChild(canvas)
    }
  }

  function fail(message: string, reason?: unknown): void {
    setText(subtitle, message)
    clear(steps)
    clear(qrHolder)
    setText(codeBox, '········')
    const detail = reason instanceof Error ? reason.message : reason ? String(reason) : ''
    setText(
      note,
      detail ? T('Səbəb: ') + detail : T('Şəbəkə bərpa olunanda səhifəni yeniləyin.'),
    )
  }

  /** Vaxtı keçmiş seans: ölü kod nə göstərilir, nə də poll olunur. */
  function expire(): void {
    start = null
    missedTicks = 0
    setText(subtitle, T('Kod hazırlanır…'))
    clear(steps)
    clear(qrHolder)
    setText(codeBox, '········')
  }

  function beginSession(): void {
    if (stopped || starting) return
    starting = true
    startQrLogin().then(
      (session) => {
        starting = false
        missedTicks = 0
        if (!stopped) render(session)
      },
      (error: unknown) => {
        starting = false
        if (!stopped) fail(T('Serverə qoşulmaq alınmadı.'), error)
      },
    )
  }

  function scheduleCheck(): void {
    if (stopped) return
    pollTimer = window.setTimeout(check, POLL_INTERVAL_MS)
  }

  /*
    ⚠⚠ AVTOMATİK TƏKRAR CƏHD (2026-09-26, kod oxunuşu ilə tapıldı). ƏVVƏL: `startQrLogin`
       alınmayanda (TV soyuq açılışda Wi-Fi hələ qalxmayıb — adi hal) `start` boş qalırdı,
       `check()` yalnız növbəti tiki planlayırdı və `beginSession()` bir daha ÇAĞIRILMIRDI;
       ekranda «Serverə qoşulmaq alınmadı» app yenidən başlayana qədər qalırdı («Geri» kökdə
       udulur, rail QR-də gizlidir). İNDİ: start yoxdursa hər 3-cü tikdə (≈9 san) yenidən
       cəhd edilir; «Geri» də dərhal yeni cəhd başladır.
  */
  function check(): void {
    pollTimer = 0
    if (stopped) return
    if (!start) {
      missedTicks++
      if (missedTicks >= 3) {
        missedTicks = 0
        beginSession()
      }
      scheduleCheck()
      return
    }
    pollQrLogin(start.pollToken).then(
      (result) => {
        if (stopped) return
        if (result.status === 'ok') {
          writeSession(result.session)
          onSignedIn()
          return
        }
        /*
          2026-10-24 (prod: `qr/poll` 410, 3 gündə 38): vaxtı keçmiş kod DƏRHAL atılır.
            ƏVVƏL: `start` köhnə seansda qalırdı — yeni `qr/start` alınmayanda (şəbəkə, 429)
            ekran ölü kodu poll etməyə davam edirdi (hər 3 san bir 410) və «hər 3-cü tikdə
            yenidən cəhd» yolu heç işə düşmürdü, çünki `start` boş deyildi. Yeni kod
            gələnə qədər köhnə kod da ekranda qalırdı.
            İNDİ: `start` sıfırlanır, kod/QR silinir, yenisi istənir; alınmasa adi
            təkrar cəhd yolu (`!start` → hər 3-cü tik) işləyir.
        */
        if (result.status === 'expired') {
          expire()
          beginSession()
        }
        scheduleCheck()
      },
      () => scheduleCheck(),
    )
  }

  beginSession()
  scheduleCheck()

  return webScreen(page, () => {
    stopped = true
    if (pollTimer) window.clearTimeout(pollTimer)
  })
}

/** «https://» olmadan göstərilir — qısa və oxunaqlı. */
function displayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\?.*$/, '')
}

// ── «Kim baxır?» ───────────────────────────────────────────────────────────

export function createWebProfilesScreen(replaceOnPick: boolean): Screen {
  const page = webPage(true)
  page.appendChild(webTitle(T('Kim baxır?')))

  const holder = el('div')
  holder.appendChild(webEmpty(T('Yüklənir…')))
  page.appendChild(holder)

  let alive = true
  let pinEpoch = 0
  const owner = readSession()
  const ownerGeneration = sessionGeneration()
  const isCurrent = () => alive && ownerGeneration === sessionGeneration() &&
    (owner ? owner.userId : null) === (readSession() ? readSession()!.userId : null)
  /*
    ⚠⚠ PIN QAPISI (2026-08-30 — TestingAI veb TV 60 · 66; sahib tələbi 2026-08-26:
       «pin qoyulubsa profiledən profilə keçiddə pin soruşulsun, əsas da uşaq
       profilindən böyük profilə keçiddə»).
       NƏ ÖLÇÜLDÜ (canlı veb TV): hasPin=true profilin kartına klik edildi — sinif
       adında «pin» olan 0 element çəkildi, heç bir `/pin` ünvanına sorğu getmədi,
       `kb_tv_profile` dərhal yazıldı və ana ekran açıldı; uşaq profilindən
       («Ləman», data-kids=12) PIN-li BÖYÜK profilə keçid də eyni cür maneəsiz oldu.
       ƏVVƏL NƏ OLURDU: `tile()` yalnız `writeSelectedProfileId` + `navigate`
       edirdi, `profile.hasPin` ÜMUMİYYƏTLƏ oxunmurdu — pad yalnız cihaz
       build-ində vardı.
    ⚠ PAD ORTAQDIR (`ui/pinPad.ts`), ikinci nüsxə YAZILMIR — səbəb orada.
    ⚠ QAT ƏVVƏLCƏDƏN QURULMUR: DOM yalnız PIN-li profil seçiləndə yaradılır.
  */
  let pinPad: PinPad | null = null
  /** PIN pad-ını açan profil kartı — bağlananda fokus ora qaytarılır. */
  let padSource: HTMLElement | null = null
  void getProfilesResult().then((result) => {
    // Refresh sessiyanı sildikdə köhnə cavab qoruyucusu çıxış mesajını udmamalıdır.
    if (!alive || (!isCurrent() && !(result.status === 'signedOut' && !readSession()))) return
    clear(holder)
    // ⚠ 401 «profil yoxdur» DEYİL (bax `lib/profiles.ts` qeydi) — istifadəçiyə
    //   göstəriləcək yeganə doğru mesaj «yenidən bağla»dır.
    if (result.status === 'signedOut') {
      holder.appendChild(webEmpty(T('Sessiya bitib. QR ilə yenidən bağlayın.')))
      holder.appendChild(centered(webButton(T('QR ilə bağla'), () => navigate({ name: 'qr' }))))
      return
    }
    // ⚠ `error` «profil yoxdur» DEYİL, şəbəkə/server nasazlığıdır — ayrıca mesaj
    //   olmasa istifadəçi profilini itirdiyini sanardı (`lib/profiles.ts` qeydi).
    if (result.status !== 'ok') {
      holder.appendChild(webEmpty(T('Profillər yüklənmədi. Bir azdan yenidən yoxlayın.')))
      holder.appendChild(centered(webButton(T('Yenidən cəhd et'), () => navigate({ name: 'profiles', replaceOnPick }, true), true)))
      return
    }
    if (!result.profiles.length) {
      holder.appendChild(webEmpty(T('Profil tapılmadı. Saytda və ya telefonda profil yaradın.')))
      return
    }
    /*
      ⚠⚠ TƏK PROFİL AVTOMATİK SEÇİLİR (MYTasks 575, sahib sözü: «QR girişindən sonra bir
         profil varsa avtomatik seçilsin»; 2026-10-02 təkrar: «bir profil varsa auto giriş
         eləsin kim baxır profil soruşmasın»).
         NƏ ÖLÇÜLDÜ (2026-10-02, canlı veb TV, brauzer, tək profilli demo hesab): QR ilə
         girişdən sonra «Kim baxır?» açıldı. Qayda yalnız cihaz paketinin ekranında
         (`screens/profiles.ts` → `autoPick`) yazılmışdı, brauzer variantının bu ekranı
         onu heç vaxt daşımamışdı. Şərtlər cihazdakı ilə eynidir: yalnız `replaceOnPick`
         (QR/boot yolu — «Profil dəyiş» qəsdli addımdır) və həqiqi UUID.
    */
    const only = result.profiles.length === 1 ? result.profiles[0] : undefined
    if (replaceOnPick && only && !needsPin(only) && isRealProfileId(only.id)) {
      pick(only)
      return
    }
    let onlyTile: HTMLElement | null = null
    for (let i = 0; i < result.profiles.length; i++) {
      const profile = result.profiles[i]
      if (!profile) continue
      const node = tile(profile)
      if (profile === only) onlyTile = node
      holder.appendChild(node)
    }
    /*
      ⚠⚠ TƏK PIN-Lİ PROFİL PIN-İ DƏRHAL SORUŞUR (2026-10-25, sahib qərarı 23: «tək profil: PIN
         yoxdursa avtomatik aç, PIN varsa PIN soruş; bir neçə profildə seçim əvvəlki kimi»). ƏVVƏL
         QR girişindən sonra tək PIN-li profil yalnız kart kimi görünürdü — pad karta basandan sonra
         açılırdı. İndi QR/boot yolunda (`replaceOnPick`) pad özü açılır; bağlansa kart qalır (pad-ı
         yenidən açır) və fokus ona qayıdır. Cihaz skini: `screens/profiles.ts → askSinglePin`.
    */
    if (replaceOnPick && only && onlyTile && needsPin(only) && isRealProfileId(only.id)) openPinPad(only, onlyTile)
  })

  function tile(profile: Profile): HTMLElement {
    const node = document.createElement('button')
    node.type = 'button'
    node.className = 'web-tile web-focusable'

    const face = el('div', 'web-tile__face', initial(profile.name))
    node.appendChild(face)
    node.appendChild(el('div', 'web-tile__name', profile.name))

    node.onclick = () => {
      if (needsPin(profile)) {
        openPinPad(profile, node)
        return
      }
      pick(profile)
    }
    return node
  }

  /**
   * PIN YALNIZ PROFİL DƏYİŞƏNDƏ soruşulur (sahib qaydası 2026-10-19) — cihaz
   * `screens/profiles.ts` → `needsPin` ilə eyni qayda.
   *
   * 2026-10-25, kod oxunuşu: artıq AKTİV olan PIN-li profilin kartı da pad açırdı. ƏVVƏL
   * istifadəçi heç nəyi dəyişmədən öz PIN-ini yenidən yazırdı. QR girişi seçimi sıfırlayır
   * (`browser/main.ts`), yəni girişdən sonrakı tək PIN-li profil yenə PIN istəyir.
   */
  function needsPin(profile: Profile): boolean {
    return profile.hasPin && profile.id !== readSelectedProfileId()
  }

  /**
   * Profili seç və ana ekrana keç — cihaz `screens/profiles.ts` → `pick()`-in eynisi.
   *
   * ⚠⚠ SIRA ƏHƏMİYYƏTLİDİR: `applyViewerAgeLimit` NAVİQASİYADAN ƏVVƏL çağırılır
   *    (2026-08-30 — TestingAI veb TV 70 · 75 · 76). NƏ ÖLÇÜLDÜ: böyük profildən
   *    uşaq profilinə («Ləman», maxAgeRating 12) keçəndə profil dəyişikliyinin
   *    açdığı ana ekran sorğusu SÜZGƏCSİZ gedirdi —
   *    `GET /api/catalog/home?platform=tv&lang=az&surface=webtv` (maxAge YOX) — və
   *    +4/+14/+29 saniyədə 171 kartın 53-ü 16+/18+ idi («18+ Pozğun», «18+ Olimpia»),
   *    `<html>`-də isə ~15 saniyə data-kids ümumiyyətlə qoyulmurdu; kartlar klik
   *    edilə bilirdi. Tərs istiqamətdə (uşaq → böyük) sorğu hələ də `maxAge=12`
   *    daşıyırdı və `data-kids="12"` + `kb_tv_max_age="12"` ~27 saniyə qalırdı.
   *    ƏVVƏL NƏ OLURDU: `tile()` `applyViewerAgeLimit`-i heç çağırmırdı; həddi
   *    yalnız `lib/ageGate.ts` → `applyKidsLimit` uzun-sorğusu gec-gec təmizləyirdi
   *    (o da ana ekranı YENİDƏN ÇƏKMİR).
   *    NİYƏ MƏHZ BU HƏLL: `maxAge` sorğuya `lib/compat.ts`-də SAXLANCDAN oxunub
   *    əlavə olunur, yəni dəyər `navigate()`-dən — və onun açdığı
   *    `browser/main.ts` → `reloadHomeData()`-dan — ƏVVƏL yazılmalıdır ki, ELƏ İLK
   *    sorğu düzgün süzgəci daşısın.
   */
  function pick(profile: Profile): void {
    if (!isCurrent()) return
    writeSelectedProfileId(profile.id)
    // Uşaq profilində kataloq süzülür və dizayn dəyişir — izahı `applyViewerAgeLimit`-də.
    applyViewerAgeLimit(profile)
    navigate({ name: 'home' }, replaceOnPick)
  }

  function openPinPad(profile: Profile, source?: HTMLElement): void {
    if (pinPad || !isCurrent()) return
    const asked = ++pinEpoch
    // «Geri» pad-ı bağlayanda fokus MƏHZ bu karta qayıdır (izahı `screen.onBack`-də).
    padSource = source || null
    pinPad = createPinPad(
      profile.name,
      (pin) =>
        verifyProfilePin(profile.id, pin).then((ok) => {
          if (!ok || asked !== pinEpoch || !pinPad || !isCurrent()) return false
          closePinPad()
          pick(profile)
          return true
        }),
      closePinPad,
    )
    page.appendChild(pinPad.node)
    pinPad.takeFocus()
  }

  function closePinPad(): void {
    pinEpoch += 1
    if (!pinPad) return
    pinPad.destroy()
    if (pinPad.node.parentNode) pinPad.node.parentNode.removeChild(pinPad.node)
    pinPad = null
  }

  /*
    ⚠⚠ «GERİ» PAD AÇIQ OLANDA PAD-I BAĞLAYIR, EKRANI DEYİL (2026-09-01).

       NƏ ÖLÇÜLDÜ (tv.kinobox.az, Playwright, real `Escape` basılışı): PIN pad-ı
       açıqkən «geri» basılanda pad EKRANDA QALIRDI. Səbəb: `webScreen`-in öz
       `onKey`-i «geri»ni birbaşa `back()`-ə ötürür (`browser/ui.ts`) və açıq
       pad-dan xəbəri yoxdur; cihaz hədəfində isə pad öz `onKey`-ini alır
       (`ui/pinPad.ts` → `key === 'back'` → `onCancel`).
       ƏVVƏL NƏ OLURDU: istifadəçi pad-dan yalnız «Ləğv et» düyməsi ilə çıxa
       bilirdi — pultda geri düyməsi işləmirdi.
       NİYƏ MƏHZ BU HƏLL: qərar bir yerdə qalır — ekranın `onKey`-i əvvəlcə pad-a
       baxır, yoxdursa doğma davranışa keçir. Pad-ın öz məntiqi (`ui/pinPad.ts`)
       toxunulmur, yəni cihaz hədəfi ilə davranış eyniləşir.
  */
  const screen = webScreen(page, () => {
    alive = false
    closePinPad()
  })
  const baseOnKey = screen.onKey
  screen.onKey = function (key) {
    if (pinPad) {
      pinPad.onKey(key)
      return
    }
    baseOnKey(key)
  }
  /*
    ⚠⚠ «GERİ» ÜÇÜN AYRICA QARMAQ (2026-09-07-də ölçüldü): yuxarıdakı `onKey` örtüyü
       BRAUZERDƏ heç vaxt çağırılmır — qabıq «geri»ni birbaşa `back()`-ə verir
       (`browser/shell.ts`). Ölçmə: PIN pad-ı açıqkən `Escape` basıldı, pad EKRANDA
       QALDI (`padOpen: true`). İndi qabıq əvvəlcə bunu soruşur.
    ⚠ FOKUS PROFİL KARTLARINA QAYIDIR: pad bağlananda ekran fokusu geri alır,
      yoxsa halqa heç bir elementdə qalmazdı (10-foot naviqasiyada ölü hal).
  */
  screen.onBack = function () {
    if (!pinPad) return false
    closePinPad()
    // ⚠ Brauzer hədəfində `takeFocus()` QƏSDƏN boşdur (`browser/ui.ts`) — fokus
    //   halqasını `:focus` idarə edir. Ona görə kart birbaşa fokuslanır: pad
    //   silinəndə fokuslanmış element yox olur və halqa `<body>`-yə düşürdü
    //   (2026-09-07-də ölçüldü: `activeElement` səhifə konteyneri idi).
    // 2026-10-23: `Node.isConnected` Chrome 54+-dır — Chrome 53-də (webOS 4.0) `undefined`
    //   qaytarırdı və fokus HEÇ VAXT karta qayıtmırdı (`<body>`-də qalırdı). `contains`
    //   hər mühərrikdə var və eyni sualı verir: element hələ sənəddədirmi.
    if (padSource && document.documentElement.contains(padSource)) padSource.focus()
    padSource = null
    return true
  }
  return screen
}

function centered(node: HTMLElement): HTMLElement {
  const wrap = el('div')
  wrap.style.textAlign = 'center'
  wrap.appendChild(node)
  return wrap
}

function initial(name: string): string {
  return name ? name.charAt(0).toUpperCase() : '?'
}

// ── Profil / Hesab ─────────────────────────────────────────────────────────

export function createWebProfileScreen(): Screen {
  const page = webPage(true)
  page.appendChild(webTitle(T('Profil')))

  const session = readSession()
  const list = el('div')
  page.appendChild(list)

  function info(label: string, value: string): HTMLElement {
    const row = el('div', 'web-list__item')
    row.appendChild(document.createTextNode(label))
    const node = el('span', 'web-list__value', value)
    row.appendChild(node)
    list.appendChild(row)
    return node
  }

  function action(label: string, value: string, onClick: () => void): void {
    const row = document.createElement('button')
    row.type = 'button'
    row.className = 'web-list__item web-focusable'
    row.appendChild(document.createTextNode(label))
    row.appendChild(el('span', 'web-list__value', value))
    row.onclick = onClick
    list.appendChild(row)
  }

  info(T('Hesab'), session ? session.fullName || session.email || '—' : T('Giriş yoxdur'))
  // ⚠ Cihaz adının sarğısı — səbəb `screens/profile.ts`-dəki eyni sətrin üstündədir
  //   (dəyər SERVERƏ tərcüməsiz getməlidir, yalnız ekranda tərcümə olunur).
  info(T('Cihaz'), T(deviceLabel()))
  info(T('Giriş rejimi'), AUTH_MODE === 'mock' ? T('Mock (nümayiş)') : T('Real (QR)'))

  // Dil — çiplər bir sətirdə (cihaz ekranındakı eyni forma).
  const langRow = el('div', 'web-list__item')
  langRow.appendChild(document.createTextNode(T('Dil')))
  const langBox = el('span', 'web-list__value')
  langRow.appendChild(langBox)
  list.appendChild(langRow)
  for (let i = 0; i < LANGUAGES.length; i++) {
    const language = LANGUAGES[i]
    if (!language) continue
    langBox.appendChild(
      webChip(language.native, language.code === readLanguage(), () => {
        // ⚠ SEÇİLMİŞ ÇİPƏ TƏKRAR BASILANDA HEÇ NƏ EDİLMİR: `writeLanguage` onsuz da
        //   erkən qayıdır, amma `reloadForLanguage` qayıtmazdı — dörd sorğu boş
        //   yerə gedərdi (`browser/shell.ts` və `screens/profile.ts`-dəki eyni şərt).
        if (language.code === readLanguage()) return
        writeLanguage(language.code)
        // ⚠⚠ DATA DA YENİDƏN ÇƏKİLİR (2026-08-30). ÖLÇÜLDÜ: bu çip yeganə yer idi
        //    ki, `reloadForLanguage()` çağırmırdı — üst zolaq və Profil səhifəsi
        //    tərcümə olunur, ana ekranın BOOT KEŞİ (`browser/main.ts` → `home` ·
        //    `recommended` · `continueWatching` · `watchlist`) isə köhnə dildə
        //    qalırdı: logoya basıb ana ekrana qayıdanda bütün film adları və blok
        //    başlıqları əvvəlki dildə görünürdü (yalnız F5 düzəldirdi).
        //    Çağıran GÖZLƏMİR — izahı `lib/language.ts` → `reloadForLanguage`-dədir.
        void reloadForLanguage()
        // ⚠ ÜST ZOLAQ DA YENİDƏN YAZILIR (2026-08-16 — TestingClaude veb TV 42):
        //   o, boot-da bir dəfə qurulur və ekranla birlikdə çəkilmir; əvvəl dil
        //   dəyişəndə səhifə tərcümə olunur, rail isə azərbaycanca qalırdı
        //   (yalnız tam yeniləmədən sonra düzəlirdi).
        repaintWebNav()
        // ⚠ Ekran yenidən qurulur (DOM bir dəfə qurulur qaydası) — `replace` ilə,
        //   yığın 20 dil dəyişikliyindən sonra şişməsin.
        navigate({ name: 'profile' }, true)
      }),
    )
  }

  // «Sonra İzlə» siyahısına yeganə keçid — cihaz `screens/profile.ts`-dəki eyni
  // qərar (üst zolağa bənd ƏLAVƏ EDİLMİR: şəxsi siyahının yeri Profildir).
  action(T('Sonra İzlə'), '→', () => navigate({ name: 'list', kind: 'watchlist' }))
  action(T('Profil dəyiş'), T('Kim baxır? →'), () => navigate({ name: 'profiles', replaceOnPick: false }))
  action(T('Cihazı yenidən bağla'), 'QR →', () => navigate({ name: 'qr' }))
  action(T('Çıxış'), '→', () => {
    signOut()
    writeSelectedProfileId(null)
    /*
      2026-10-23: UŞAQ QATI ÇIXIŞDA DA DÜŞÜR (memory: kids-layer-must-not-leak-on-profile-switch).
        NƏ TAPILDI: çıxış profili silirdi, amma `applyViewerAgeLimit(null)` çağırılmırdı —
        `<html data-kids>` və saxlancdakı `kb_tv_max_age` qalırdı.
        ƏVVƏL: QR ekranı uşaq temasında açılırdı və `maxAge=` hər sorğuya gedirdi — növbəti
        girişə qədər, hətta səhifə yeniləndikdən sonra da (`restoreViewerAgeLimit` saxlancdan
        bərpa edir).
        NİYƏ BELƏ: QR girişinin öz geri çağırışı (`main.ts`) eyni çağırışı edir — çıxış onun
        güzgüsüdür; hədd naviqasiyadan ƏVVƏL silinir ki, QR ekranı adi qatda qurulsun.
    */
    applyViewerAgeLimit(null)
    // ⚠ Yaş keşi də təmizlənir (MYTasks 577) — növbəti hesab köhnə yaşla
    //   qiymətləndirilməməlidir.
    resetAge()
    navigate({ name: 'qr' })
  })

  const selectedId = readSelectedProfileId()
  if (selectedId) {
    const activeValue = info(T('Aktiv profil'), '—')
    void getProfilesResult().then((result) => {
      if (result.status !== 'ok') return
      for (let i = 0; i < result.profiles.length; i++) {
        const profile = result.profiles[i]
        if (profile && profile.id === selectedId) {
          setText(activeValue, profile.name)
          return
        }
      }
    })
  }

  return webScreen(page)
}
